import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../../../../App";
import RbacGate from "../../../../../../components/RbacGate";
import {
  fetchSubscriptionBundle,
  resolveContext,
} from "../../../../../../services/subscriptionService";
import "./index.css";
import { getStoreCurrencySymbol } from "../../../../../../utils/currency";

/* ══════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════ */

/** Format "DD-MM-YYYY" or "YYYY-MM-DD" → "DD Mon YYYY" */
function formatDate(raw) {
  if (!raw) return "—";
  const s = String(raw).trim();
  let day, mon, year;
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  if (parts[0].length === 4) {
    // ISO: YYYY-MM-DD
    [year, mon, day] = parts;
  } else {
    // DD-MM-YYYY
    [day, mon, year] = parts;
  }
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const label = months[parseInt(mon, 10) - 1] || mon;
  return `${day} ${label} ${year}`;
}

/** Currency display */
function inr(amount) {
  if (amount === null || amount === undefined) return "—";
  const n = Number(amount);
  if (isNaN(n)) return "—";
  const sym = getStoreCurrencySymbol();
  if (n === 0) return `${sym}0`;
  if (n >= 10_000_000) return `${sym}${(n / 10_000_000).toFixed(2).replace(/\.?0+$/, "")}Cr`;
  if (n >= 100_000) return `${sym}${(n / 100_000).toFixed(2).replace(/\.?0+$/, "")}L`;
  return `${sym}${n.toLocaleString("en-IN")}`;
}

/** Format epoch ms → "4 Aug 09:30 AM" */
function fmtFetchedAt(ms) {
  if (!ms) return null;
  const d = new Date(ms);
  if (isNaN(d)) return null;
  const day = d.getDate();
  const mon = d.toLocaleString("en-US", { month: "short" });
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = String(h % 12 || 12);
  return `${day} ${mon} ${h12}:${m} ${ampm}`;
}

/** Resolve the name of an addon from catalog detail */
function addonName(detail) {
  if (!detail) return null;
  return detail.packageName || detail.addonName || detail.name || detail.title || null;
}

/** Walk all response envelope variants for addon details → array */
function extractAddonArray(responseData) {
  if (!responseData) return [];
  // Variant D: top-level array
  if (Array.isArray(responseData)) return responseData;
  // Variant A: { status, data: [...] }
  if (Array.isArray(responseData.data)) return responseData.data;
  // Variant B: { status, data: { data: [...] } }
  if (responseData.data && Array.isArray(responseData.data.data)) return responseData.data.data;
  // Variant C: { status, data: { addons: [...] } }
  if (responseData.data && Array.isArray(responseData.data.addons)) return responseData.data.addons;
  return [];
}

/** StatusBadge component */
function StatusBadge({ status }) {
  if (!status) return null;
  const s = String(status).toUpperCase();
  const cls =
    s === "ACTIVE"
      ? "as-status-active"
      : s === "INACTIVE" || s === "EXPIRED"
      ? "as-status-inactive"
      : "as-status-other";
  return (
    <span className={`as-status-badge ${cls}`}>
      <span className="as-status-dot" />
      {status}
    </span>
  );
}

/** A single label/value row */
function DataRow({ label, value, valueClass }) {
  return (
    <div className="as-row">
      <span className="as-row-label">{label}</span>
      <span className={`as-row-value ${valueClass || ""}`}>{value || "—"}</span>
    </div>
  );
}

/** A single addon row */
function AddonRow({ addon }) {
  if (!addon) return null;
  const name = addon?.packageName || addon?.addonId;
  const hasName = !!addon?.packageName;
  const desc = addon?.shortDescription || addon?.description || null;
  const truncDesc = desc && desc.length > 120 ? desc.slice(0, 120) + "…" : desc;

  return (
    <div className="as-addon-row">
      <div className="as-addon-left">
        <span className="as-addon-name">{name}</span>
        {truncDesc && <span className="as-addon-desc">{truncDesc}</span>}
        <div className="as-addon-badges">
          <StatusBadge status={addon?.status} />
          {addon?.category && <span className="as-category-pill">{addon.category}</span>}
        </div>
        {addon?.billingCycle && <span className="as-addon-cycle">{addon.billingCycle}</span>}
        {hasName && addon?.addonId && <span className="as-addon-id">{addon.addonId}</span>}
      </div>
      <div className="as-addon-right">
        {addon?.netPrice === 0 ? (
          <span className="as-addon-price-free">Free</span>
        ) : (
          <span className="as-addon-price">{inr(addon?.netPrice)}</span>
        )}
        {(addon?.startDate || addon?.endDate) && (
          <span className="as-addon-dates">
            {formatDate(addon?.startDate)} – {formatDate(addon?.endDate)}
          </span>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Main component
   ══════════════════════════════════════════════════════════ */
const CACHE_KEY = "storeSubscription";

export default function AccountSubscription() {
  const navigate = useNavigate();
  const { color } = useContext(UserContext);

  /* ── RBAC: handled by <RbacGate> wrapper below ── */

  const { orgId, locId, orgName, locName, accountId } = resolveContext();
  const subtitle =
    orgName && locName ? `${orgName} · ${locName}` : orgName ? orgName : "Store Details";

  const [bundle, setBundle] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      // Discard cache saved with a missing accountId (API error)
      if (cached?.subscriptionMessage === "AccountId and OrganizationId are mandatory") return null;
      // Discard cache where the stored subscription is EXPIRED — there may be a newer
      // ACTIVE entry in the API response that wasn't selected when the cache was written.
      if (cached?.subscription && cached.subscription.status !== "ACTIVE") return null;
      return cached;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runFetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSubscriptionBundle(accountId, orgId, locId);
      setBundle(result);
      localStorage.setItem(CACHE_KEY, JSON.stringify(result));
      /* Notify SubscriptionContext so AddonGate re-evaluates immediately */
      window.dispatchEvent(new CustomEvent("pos:subscriptionLoaded"));
    } catch (err) {
      console.error("AccountSubscription fetch error:", err);
      setError("Failed to load subscription data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [accountId, orgId, locId]);

  // Fetch on mount if no cache
  useEffect(() => {
    if (!bundle) runFetch();
  }, []); // eslint-disable-line

  /* Determine empty state message */
  const noAccountId = !accountId;
  const subMsg = bundle?.subscriptionMessage;
  let emptyMsg = null;
  if (!bundle) {
    emptyMsg = noAccountId
      ? "No account ID found for this store. Subscription data will load after a fresh login."
      : "Subscription data not yet loaded. Tap Refresh to fetch the latest details.";
  } else if (
    !bundle.subscription &&
    !bundle.upcomingBilling &&
    (!bundle.addons || bundle.addons.length === 0)
  ) {
    emptyMsg =
      subMsg === "SUBSCRIPTION_NOT_ACTIVE"
        ? "This store does not have an active subscription."
        : subMsg && subMsg !== "Success"
        ? subMsg
        : "No subscription, billing, or add-on data found for this store.";
  }

  const hasAnyData =
    bundle &&
    (bundle.subscription || bundle.upcomingBilling || (bundle.addons && bundle.addons.length > 0));
  const fetchedLabel = bundle?.fetchedAt ? fmtFetchedAt(bundle.fetchedAt) : null;

  /* Plan features */
  const [featOpen, setFeatOpen] = useState(false);
  const features = bundle?.upcomingBilling?.features?.filter((f) => f?.displayFlag) || [];
  const featGroups = features.reduce((acc, f) => {
    const g = f?.featureCategory || "General";
    if (!acc[g]) acc[g] = [];
    acc[g].push(f);
    return acc;
  }, {});

  /* Addons split */
  const activeAddons = (bundle?.addons || []).filter((a) => a?.status === "ACTIVE");
  const expiredAddons = (bundle?.addons || []).filter((a) => a && a?.status !== "ACTIVE");

  const isDark = color === "dark";

  return (
    <RbacGate screenId="account-subscription">
      <div className={`as-container${isDark ? " dark" : ""}`}>
        {/* Header */}
        <div className="as-header">
          <button
            className="as-back"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate("/settings");
            }}
            aria-label="Go back"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <div>
            <p className="as-header-title">Account &amp; Subscription</p>
            <p className="as-header-sub">{subtitle}</p>
          </div>
        </div>

        {/* Body */}
        <div className="as-body">
          {/* Meta chips */}
          <div className="as-chips-row">
            {accountId && (
              <span className="as-chip as-chip-blue">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {accountId}
              </span>
            )}
            {orgId && (
              <span className="as-chip as-chip-navy">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
                {orgId}
              </span>
            )}
            {locId && (
              <span className="as-chip as-chip-navy">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {locId}
              </span>
            )}
          </div>

          {/* Last updated + refresh */}
          <div className="as-meta-row">
            {fetchedLabel && <span className="as-updated">Last updated: {fetchedLabel}</span>}
            <button
              className="as-refresh-btn"
              onClick={runFetch}
              disabled={loading}
              style={!fetchedLabel ? { marginLeft: "auto" } : undefined}
            >
              {loading ? (
                <>
                  <span className="as-refresh-spinner" />
                  Refreshing…
                </>
              ) : (
                <>
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                    <path d="M21 3v5h-5" />
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                    <path d="M8 16H3v5" />
                  </svg>
                  Refresh Subscription Data
                </>
              )}
            </button>
          </div>

          {/* Error */}
          {error && !loading && (
            <div style={{ fontSize: 12, color: "#dc2626", padding: "4px 2px" }}>{error}</div>
          )}

          {/* Empty state */}
          {!loading && emptyMsg && (
            <div className="as-empty">
              <div className="as-empty-icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
                  <path d="M14 8H8M16 12H8M12 16H8" />
                </svg>
              </div>
              <p className="as-empty-title">No Subscription Data</p>
              <p className="as-empty-msg">{emptyMsg}</p>
            </div>
          )}

          {/* ── Subscription card ── */}
          {bundle?.subscription && (
            <div className="as-card">
              <div className="as-card-header as-card-header-blue">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                </svg>
                Active Subscription
              </div>
              <div className="as-plan-banner">
                <span className="as-plan-name">
                  {bundle.subscription.packageName || bundle.subscription.subscriptionId}
                </span>
                <StatusBadge status={bundle.subscription.status} />
              </div>
              <div className="as-rows">
                <DataRow
                  label="Subscription ID"
                  value={bundle.subscription.accountSubscriptionId}
                />
                <DataRow label="Account ID" value={bundle.subscription.accountId} />
                <DataRow label="Billing Cycle" value={bundle.subscription.billingCycle} />
                <DataRow label="Start Date" value={formatDate(bundle.subscription.startDate)} />
                <DataRow label="End Date" value={formatDate(bundle.subscription.endDate)} />
                <DataRow label="Renewal Date" value={formatDate(bundle.subscription.renewalDate)} />
              </div>
            </div>
          )}

          {/* ── Upcoming Billing card ── */}
          {bundle?.upcomingBilling && (
            <div className="as-card">
              <div className="as-card-header as-card-header-purple">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                Upcoming Billing
              </div>
              <div className="as-billing-banner">
                <div className="as-billing-banner-left">
                  <span className="as-billing-label">Next Payment</span>
                  <span className="as-billing-amount">
                    {inr(bundle.upcomingBilling.nextPaymentAmount)}
                  </span>
                </div>
                <div className="as-billing-banner-right">
                  <span className="as-billing-label">Due in</span>
                  <span className="as-billing-due-days">
                    {bundle.upcomingBilling.daysUntilNextPayment ?? "—"} days
                  </span>
                </div>
              </div>
              <div className="as-rows">
                <DataRow
                  label="Next Billing Date"
                  value={formatDate(bundle.upcomingBilling.nextBillingDate)}
                />
                <DataRow
                  label="Renewal Date"
                  value={formatDate(bundle.upcomingBilling.renewalDate)}
                />
                <DataRow label="Plan" value={bundle.upcomingBilling.planName} />
                <DataRow label="Base Price" value={inr(bundle.upcomingBilling.basePrice)} />
                <DataRow
                  label="GST"
                  value={
                    bundle.upcomingBilling.gstRate != null
                      ? `${Math.round(bundle.upcomingBilling.gstRate * 100)}% (${inr(
                          bundle.upcomingBilling.totalTaxAmount
                        )})`
                      : "—"
                  }
                />
                <DataRow
                  label="Total Amount"
                  value={inr(bundle.upcomingBilling.totalAmountWithTax)}
                  valueClass="as-row-value-purple"
                />
              </div>

              {/* Plan features collapsible */}
              {features.length > 0 && (
                <>
                  <button className="as-features-toggle" onClick={() => setFeatOpen((o) => !o)}>
                    <span>
                      {featOpen ? "Hide Features" : `View ${features.length} Plan Features`}
                    </span>
                    <svg
                      className={`as-features-chevron${featOpen ? " open" : ""}`}
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                  {featOpen && (
                    <div className="as-features-body">
                      {Object.entries(featGroups).map(([group, items]) => (
                        <div key={group}>
                          <div className="as-feat-group-label">{group}</div>
                          {items.map((f) => (
                            <div key={f?.featureId || f?.featureName} className="as-feat-row">
                              {f?.available ? (
                                <svg
                                  className="as-feat-icon-ok"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                  <path d="m9 11 3 3L22 4" />
                                </svg>
                              ) : (
                                <svg
                                  className="as-feat-icon-no"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="m15 9-6 6M9 9l6 6" />
                                </svg>
                              )}
                              {f?.featureName}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Active Add-ons card ── */}
          {(bundle?.subscription || (bundle?.addons && bundle.addons.length > 0)) && (
            <div className="as-card">
              <div className="as-card-header as-card-header-amber">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4l3 3" />
                </svg>
                Active Add-ons
              </div>
              <div className="as-addon-list">
                {activeAddons.length === 0 ? (
                  <div className="as-addon-empty">No active add-ons</div>
                ) : (
                  activeAddons.map((a) => (
                    <AddonRow key={a?.accountAddonId || a?.addonId} addon={a} />
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Expired Add-ons card ── */}
          {expiredAddons.length > 0 && (
            <div className="as-card">
              <div className="as-card-header as-card-header-red">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="m15 9-6 6M9 9l6 6" />
                </svg>
                Expired / Inactive Add-ons
              </div>
              <div className="as-addon-list">
                {expiredAddons.map((a) => (
                  <AddonRow key={a?.accountAddonId || a?.addonId} addon={a} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </RbacGate>
  );
}
